// Year helpers for filtering invoices / pay stubs by calendar year.

// Returns the 4-digit year of an ISO date string ('2026-03-14'), Firestore Timestamp or Date, or null.
// ISO strings are read directly so a date like '2026-01-01' never shifts to 2025 via UTC parsing.
export function getYear(value) {
  if (!value) return null
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-/)
    if (match) return Number(match[1])
  }
  if (typeof value === 'object' && value.seconds !== undefined) {
    return new Date(value.seconds * 1000).getFullYear()
  }
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.getFullYear()
}

// Returns the month (1–12) of the same value types as getYear, or null.
export function getMonth(value) {
  if (!value) return null
  if (typeof value === 'string') {
    const match = value.match(/^\d{4}-(\d{2})-/)
    if (match) return Number(match[1])
  }
  if (typeof value === 'object' && value.seconds !== undefined) {
    return new Date(value.seconds * 1000).getMonth() + 1
  }
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.getMonth() + 1
}

// A Date for display. 'YYYY-MM-DD' strings become local midnight (new Date('2026-10-19') would be
// UTC midnight, which shows as Oct 18 in US time zones). Also accepts Firestore Timestamps and Dates.
export function toLocalDate(value) {
  if (value && typeof value.toDate === 'function') return value.toDate()
  if (typeof value === 'object' && value?.seconds !== undefined) return new Date(value.seconds * 1000)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  return value instanceof Date ? value : new Date(value)
}

// Sortable millisecond value for the same value types as getYear (0 when missing/invalid).
export function toTime(value) {
  if (!value) return 0
  if (typeof value === 'object' && value.seconds !== undefined) return value.seconds * 1000
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number)
    return new Date(y, m - 1, d).getTime()
  }
  const t = (value instanceof Date ? value : new Date(value)).getTime()
  return Number.isNaN(t) ? 0 : t
}

// Distinct years found in `records[field]`, always including the current year, newest first.
export function yearOptions(records, field) {
  const years = new Set([new Date().getFullYear()])
  for (const r of records) {
    const y = getYear(r[field])
    if (y) years.add(y)
  }
  return [...years].sort((a, b) => b - a)
}
